// src/components/customers/forms/EditCustomerForm.tsx
import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import Select from "@/components/shared/ui/Select";
import LocationRow, {
  type LocationFormValue as CSCFormValue,
} from "@/components/shared/forms/LocationRow";

import {
  FiMail,
  FiPhone,
  FiUser,
  FiCalendar,
  FiHash,
  FiHome,
  FiMapPin,
} from "react-icons/fi";

/* =========================
 * Types
 * ========================= */

export type Gender = "Male" | "Female" | "Other";
export type IdType = "National ID" | "Passport";

/**
 * UI-level Customer type for the edit form.
 * - Includes username.
 * - No role or password (role = Customer, password system-generated).
 */
export type Customer = {
  id: number | string;

  firstName: string;
  lastName: string;
  username: string;

  email: string;
  phone: string;

  status: "ACTIVE" | "INACTIVE" | "BLOCKED";

  gender?: Gender;
  dob?: string; // yyyy-mm-dd
  idType?: IdType;
  idNumber?: string;

  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  state?: string;
  city?: string;
};

export default function EditCustomerForm({
  customer,
  onSubmit,
  submitting = false,
  onCancel,
  errorMessage,
}: {
  customer: Customer;
  onSubmit: (payload: Customer) => void;
  submitting?: boolean;
  onCancel?: () => void;
  errorMessage?: string | null;
}) {
  const [form, setForm] = useState<Customer>({ ...customer });
  const [localError, setLocalError] = useState<string | null>(null);

  const change = <K extends keyof Customer>(k: K, v: Customer[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (localError) setLocalError(null);
  };

  /* =========================
   * Submit / Cancel
   * ========================= */
  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.username.trim() ||
      !form.email.trim()
    ) {
      setLocalError(
        "Please fill in first name, last name, username, and email."
      );
      return;
    }

    // Normalize DOB: "" → undefined
    const cleaned: Customer = {
      ...form,
      dob: form.dob && form.dob.trim() ? form.dob : undefined,
    };

    setLocalError(null);
    onSubmit(cleaned);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.back();
    }
  };

  const bannerMessage = localError || errorMessage || null;

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      {/* Top error banner */}
      {bannerMessage && (
        <div
          className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
          aria-live="polite"
        >
          {bannerMessage}
        </div>
      )}

      {/* Name */}
      <Field label="First name" icon={<FiUser />}>
        <Input
          value={form.firstName}
          onChange={(e) => change("firstName", e.target.value)}
          placeholder="Enter first name"
          required
        />
      </Field>
      <Field label="Last name" icon={<FiUser />}>
        <Input
          value={form.lastName}
          onChange={(e) => change("lastName", e.target.value)}
          placeholder="Enter last name"
          required
        />
      </Field>

      {/* Username */}
      <Field label="Username" icon={<FiUser />}>
        <Input
          value={form.username}
          onChange={(e) => change("username", e.target.value)}
          placeholder="Unique username"
          required
        />
      </Field>

      {/* Contact */}
      <Field label="Email" icon={<FiMail />}>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => change("email", e.target.value)}
          placeholder="name@example.com"
          required
        />
      </Field>
      <Field label="Phone" icon={<FiPhone />}>
        <PhoneInput
          country="in"
          value={form.phone}
          onChange={(v) => change("phone", `+${v}`)}
          inputClass="!w-full !rounded-lg !border !border-gray-300 !text-sm"
          buttonClass="!border-gray-300"
        />
      </Field>

      {/* Profile */}
      <Field label="Gender" icon={<FiUser />}>
        <Select
          value={form.gender || ""}
          onChange={(e) => change("gender", e.target.value as Gender)}
          options={["Male", "Female", "Other"]}
        />
      </Field>
      <Field label="Date of birth" icon={<FiCalendar />}>
        <Input
          type="date"
          value={form.dob ?? ""}
          onChange={(e) => change("dob", e.target.value)}
          placeholder="YYYY-MM-DD"
        />
      </Field>

      {/* Identity */}
      <Field label="Identification type" icon={<FiHash />}>
        <Select
          value={form.idType || ""}
          onChange={(e) => change("idType", e.target.value as IdType)}
          options={["National ID", "Passport"]}
        />
      </Field>
      <Field label="ID number" icon={<FiHash />}>
        <Input
          value={form.idNumber ?? ""}
          onChange={(e) => change("idNumber", e.target.value)}
          placeholder="Enter ID / Passport no."
        />
      </Field>

      {/* Address section title */}
      <div className="mt-4 sm:col-span-2 text-sm font-semibold text-gray-800">
        Address &amp; Contact
      </div>

      <Field label="Address line 1" icon={<FiHome />}>
        <Input
          value={form.addressLine1 ?? ""}
          onChange={(e) => change("addressLine1", e.target.value)}
          placeholder="House / Flat / Street"
        />
      </Field>
      <Field label="Address line 2" icon={<FiMapPin />}>
        <Input
          value={form.addressLine2 ?? ""}
          onChange={(e) => change("addressLine2", e.target.value)}
          placeholder="Area / Landmark (optional)"
        />
      </Field>

      {/* Country → Region → City, sourced from Settings → Locations */}
      <div className="sm:col-span-2">
        <LocationRow
          value={{
            country: form.country ?? undefined,
            state: form.state ?? undefined,
            city: form.city ?? undefined,
          }}
          onChange={(v: CSCFormValue) => {
            setForm((p) => ({
              ...p,
              country: v.country || "",
              state: v.state || "",
              city: v.city || "",
            }));
            if (localError) setLocalError(null);
          }}
          instancePrefix="edit-customer-csc"
        />
      </div>

      {/* Status */}
      <div className="mt-2 sm:col-span-2">
        <Field label="Customer Status" icon={<FiUser />}>
        <Select
            value={form.status || ""}
            onChange={(e) =>
              change("status", e.target.value as "ACTIVE" | "INACTIVE" | "BLOCKED")
            }
            options={["ACTIVE", "INACTIVE", "BLOCKED"]}
          />
        </Field>

        <div className="mt-1 text-xs text-gray-500">
          Active → Customer can use services.  
          Inactive → Temporarily disabled.  
          Blocked → Fully restricted (no logins, no actions).
        </div>
      </div>


      {/* Actions */}
      <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
